import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query";
import { ClientError } from "graphql-request";
import { LOG_EVENT } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { v4 as uuidv4 } from "uuid";
import { reportWhatId } from "./reportRoute";
import eventOutbox from "../store/eventOutbox";
import type { RecentEvent } from "../store/eventOutbox";
import userStore from "../store/userStore";
import { useProductFeatures } from "./useProductFeatures";
import { usePostHog } from "posthog-js/react";

export const EVENT_TYPES = {
  LOGGED_IN: "logged-in",
  LADDER_COMPLETION: "ladder-completion",
  CLIMB_COMPLETION: "climb-completion",
  SITE_VISIT_CREATION: "site-visit-creation",
  // CERTIFICATE_COMPLETION: "certificate-completion", // this one is heandled in the pipeline resolver.
  HEARD_OF_HAT: "heard-of-hat",
  SAVED_HAT: "saved-hat",
  APPLICATION_ERROR: "application-error",
  EARNED_XP: "earned-xp",
  ACTIVITY_COMPLETION: "activity-completion",
  CONTENT_COMPLETION: "content-completion",
  CLUSTER_CONTENT_COMPLETED: "cluster-content-completed",
  RESOURCE_DOWNLOAD: "resource-download",
  HAT_EXPLORED: "hat-explored",
  FACET_CHANGED: "facet-changed",
  WATCHED_VIDEO: "watched-video",
  LISTENED_TO_AUDIO: "listened-to-audio",
  PROFILE_ONBOARD_COMPLETED: "profile-onboard-completed",
  HAT_RATED: "hat-rated",
  ADD_BUILDING_BLOCK: "add-building-block",
  PATHWAY_RATED: "pathway-rated",
  RECOMMENDED_CLUSTER_FOUND: "recommended-cluster-found",
  ONBOARD_SOMETHING_ELSE: "onboard-something-else",
  CLIMBER_RESET: "climber-reset",
  CAREER_CONFIDENCE_CHECKIN: "career-confidence-checkin",
  PERK_QUIRK_BADGE_LIKED: "perk-quirk-badge-liked",
  EARNED_HAT_PACK: "earned-hat-pack",

  // Coach actions. Five of the seven in the activation spec emitted nothing at
  // all, so a coach whose work is reports, assignments and profile reviews read
  // as un-activated — 45% of coaches had ever emitted any event. These close
  // that gap; see packages/cli-util/report-bot/METRICS.md for which metric each
  // one unblocks, and add them to the IN (…) list in coach-activation.sql once
  // they are emitting.
  DASHBOARD_VIEWED: "dashboard-viewed",
  REPORT_OPENED: "report-opened",
  REPORT_DOWNLOADED: "report-downloaded",
  REPORT_FILTERED: "report-filtered",
  // Drilling into a report's detail view — school to class to climber.
  // Listed separately in the engagement spec from filtering and
  // downloading, so it gets its own type rather than being folded into
  // report-opened, which means "arrived at the report" not "went deeper".
  REPORT_DRILLDOWN: "report-drilldown",
  ASSIGNMENT_CREATED: "assignment-created",
  ASSIGNMENT_EDITED: "assignment-edited",
  CLIMBER_PROFILE_VIEWED: "climber-profile-viewed",
  CAREER_PLAN_VIEWED: "career-plan-viewed",
  CAREER_PLAN_FEEDBACK: "career-plan-feedback",
} as const;

export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

/**
 * Retry a dropped connection or a transient server failure, never a
 * rejection. `retry: true` retried forever, so an event the resolver refuses
 * (InvalidEventInput: a field wider than its Redshift column) would have been
 * re-sent until the tab closed.
 *
 * graphql-request raises ClientError for a GraphQL error AND for any non-2xx
 * HTTP response. A throttle (429) or a 5xx is worth another try. A GraphQL
 * error arrives inside an HTTP 200, so its errorType decides: the resolver
 * reports a rejected Kinesis PutRecord (a throttle, a Kinesis 5xx) as
 * KinesisPutRecordError, which is transient; InvalidEventInput and anything
 * else (auth, validation) is final.
 */
const TRANSIENT_ERROR_TYPES = new Set(["KinesisPutRecordError"]);

export const shouldRetryLogEvent = (
  failureCount: number,
  error: Error,
): boolean => {
  if (failureCount >= 3) return false;
  if (error instanceof ClientError) {
    const status = error.response?.status ?? 0;
    if (status === 429 || status >= 500) return true;
    const errors = (error.response?.errors ?? []) as Array<{
      errorType?: string;
    }>;
    return (
      errors.length > 0 &&
      errors.every((e) => TRANSIENT_ERROR_TYPES.has(e.errorType ?? ""))
    );
  }
  return true;
};

// Events a coach generates as a coach, not as a climber. Every client-logged
// event is stamped with the authenticated caller as who_id (the log-event
// resolver ignores anything else), so a coach who visits Climber Mode ends up
// with one event- stream mixing their coach work and the climber profile they
// built up. A climber-mode reset has to leave these standing.
//
// Server-emitted coach actions — shout-outs, plan approvals, worksite visit
// deletes — are attributed to the climber they concern, so they are already
// outside a coach's own reset and don't need listing here.
export const EDUCATOR_EVENT_TYPES: readonly EventType[] = [
  EVENT_TYPES.LOGGED_IN,
  EVENT_TYPES.RESOURCE_DOWNLOAD,
  EVENT_TYPES.SITE_VISIT_CREATION,
  EVENT_TYPES.CLIMBER_RESET,
  // The new coach actions belong here for the same reason: they are work a coach
  // did as a coach. Omitting them would mean a climber-mode reset wiped a
  // coach's report and assignment history along with the climber profile they
  // had built up, which is exactly what this list exists to prevent.
  EVENT_TYPES.DASHBOARD_VIEWED,
  EVENT_TYPES.REPORT_OPENED,
  EVENT_TYPES.REPORT_DOWNLOADED,
  EVENT_TYPES.REPORT_FILTERED,
  EVENT_TYPES.REPORT_DRILLDOWN,
  EVENT_TYPES.ASSIGNMENT_CREATED,
  EVENT_TYPES.ASSIGNMENT_EDITED,
  EVENT_TYPES.CLIMBER_PROFILE_VIEWED,
  EVENT_TYPES.CAREER_PLAN_VIEWED,
  EVENT_TYPES.CAREER_PLAN_FEEDBACK,
] as const;

// What events should we be sending to posthog?
const POSTHOG_WHITE_LIST: readonly EventType[] = [
  EVENT_TYPES.HAT_EXPLORED,
  EVENT_TYPES.PROFILE_ONBOARD_COMPLETED,
  EVENT_TYPES.CLUSTER_CONTENT_COMPLETED,
  // EVENT_TYPES.CLIMB_COMPLETION, <-- Not called through useReporting, see queries.js
] as const;

interface LogEventMetadata {
  [key: string]: string | number | boolean | null | undefined | string[];
}

interface LogEventInput {
  whatId?: string | null;
  eventType: EventType;
  maxScore?: number | null;
  score?: number | null;
  result?: string | null;
  metadata?: LogEventMetadata;
}

interface LogEventResponse {
  logEvent: string;
}

export const useReporting = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  // Resolve the product the same way the mutations fired alongside these events
  // do. A bare userStore.getActiveProductCode() misses the sole-product
  // fallback, so a single-product climber whose activeProductCode was never
  // persisted (never passed through /post-login, cleared storage, incognito)
  // sent the mutation with a product and the matching event without one.
  const { activeProduct } = useProductFeatures();

  /*
    recentEvents: This is a bit annoying.
    When we log an event, it takes a bit of time to go through the pipeline and get saved to dynamo.
    While that's happening, we might try to getNextSteps, which needs to know about that event.

    So we hand the event to eventOutbox and pass everything it holds along with
    getNextSteps / getEarnedBadges. Those resolvers use our events if they're
    not already in dynamo, checking by the uuid inside `data`. See
    store/eventOutbox.ts for the retention and per-user rules.
  */

  const logEvent = async (input: LogEventInput): Promise<LogEventResponse> => {
    const {
      whatId = null,
      eventType,
      maxScore = null,
      score = null,
      result = null,
      metadata = {},
    } = input;

    const uuid = uuidv4();
    const graphQLInput = {
      whatId,
      eventType,
      maxScore,
      score,
      result,
      // The active product code this event was generated for (reporting).
      product: activeProduct?.code || userStore.getActiveProductCode() || null,
      metadata: JSON.stringify({
        ...metadata,
        uuid,
      }),
    };

    // Store the event in the format expected by the next steps processor
    const recentEvent: RecentEvent = {
      id: `recent-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      eventType,
      data: JSON.stringify({
        ...metadata,
        result,
        score: maxScore ? score : undefined,
        maxScore: maxScore || undefined,
        whatId,
        uuid, // UUID goes in the data for deduplication checking
      }),
      timestamp: new Date().toISOString(),
    };

    eventOutbox.add(recentEvent);

    if (POSTHOG_WHITE_LIST.includes(eventType)) {
      posthog.capture(eventType, {
        whatId,
        eventType,
        maxScore,
        score,
        result,
        uuid,
        ...metadata,
      });
    }

    return graphQLClient.request<LogEventResponse>(LOG_EVENT, {
      input: graphQLInput,
    });
  };

  const mutation = useMutation<LogEventResponse, Error, LogEventInput>({
    mutationFn: logEvent,
    retry: shouldRetryLogEvent,
    onError: (error) => {
      console.error("Error logging event:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nextSteps"] });
    },
  });

  /**
   * Coach landed on the reports dashboard — the grid itself, not a report.
   *
   * Distinct from report-opened on purpose: that one fires when a coach clicks
   * THROUGH the grid into a report, so it cannot tell you about a coach who
   * opened the dashboard and bounced. The gap between the two is the signal —
   * dashboards viewed with no report opened is a coach who looked and found
   * nothing worth clicking.
   *
   * No `result`: there is one dashboard, so a discriminator would be a constant.
   */
  const reportDashboardViewed = async (): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      eventType: EVENT_TYPES.DASHBOARD_VIEWED,
    });

  /**
   * Coach opened a report.
   *
   * The report is identified in what_id by its ROUTE (`REPORT-<slug>`, see
   * reportWhatId), not by name. The CMS name used to go in `result`, and
   * "Career Planning Progress" is 24 characters against result's VARCHAR(20):
   * Redshift rejected the row, and with it every other event in the same
   * Firehose batch. The name still travels in metadata for the raw table.
   * The route is also the stabler key — a rename in the CMS does not split
   * the series.
   */
  const reportReportOpened = async (
    reportName: string,
    route: string,
  ): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      whatId: reportWhatId(route),
      eventType: EVENT_TYPES.REPORT_OPENED,
      metadata: { reportName },
    });

  /**
   * Coach downloaded a report's CSV.
   *
   * `route` is the report the user was on, resolved by ReportTelemetry — NOT
   * the filename. The filename is scoped per school and stamped with the date,
   * so keying on it gave one series per school per day and nothing that
   * joined to report-opened. It is kept in metadata for anyone reading the raw
   * table.
   */
  const reportReportDownloaded = async (
    reportName: string,
    route: string,
    rowCount?: number,
    filename?: string,
  ): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      whatId: reportWhatId(route),
      eventType: EVENT_TYPES.REPORT_DOWNLOADED,
      result: "csv",
      score: rowCount ?? null,
      metadata: { reportName, rowCount, filename },
    });

  /**
   * Coach changed a report's filters.
   *
   * DEBOUNCE AT THE CALL SITE. A filter fires on every keystroke and every
   * dropdown change; unthrottled this is the chattiest event in the product and
   * each one writes a DynamoDB record through event-processor. The
   * application-error flood — 4.74M rows, ~417 per affected climber — is what
   * that looks like when it goes wrong. One event per settled interaction.
   */
  const reportReportFiltered = async (
    reportName: string,
    route: string,
    filters: string[],
  ): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      whatId: reportWhatId(route),
      eventType: EVENT_TYPES.REPORT_FILTERED,
      metadata: { reportName, filters },
    });

  /**
   * Coach drilled into a report's detail view. `level` names what they opened —
   * "school", "class", "climber" — so the depth is visible, not just the fact.
   * The report is what_id, the level is the result: two columns, so neither
   * has to be squeezed into the other.
   */
  const reportReportDrilldown = async (
    reportName: string,
    route: string,
    level: "school" | "class" | "climber",
  ): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      whatId: reportWhatId(route),
      eventType: EVENT_TYPES.REPORT_DRILLDOWN,
      result: level,
      metadata: { reportName, level },
    });

  /**
   * Normalise a climb id to the form the warehouse stores.
   *
   * climb-completion is written server-side as `CLIMB-${climbId.replace("cc-","")}`
   * (career-climb-progress/index.js), so what_dimension and every completion row
   * hold `CLIMB-13`. The client knows the climb as a bare CMS id, or sometimes as
   * a `cc-` progress-record id. Emitting either of those unchanged means
   * career-climbs-unassigned joins `CLIMB-13` against `13`, matches nothing, and
   * reports 100% voluntary — while its own instrumented flag says the number can
   * be trusted. That is worse than no report, so the id is normalised here rather
   * than hoped about in SQL.
   */
  const toWarehouseClimbId = (id: string | number): string => {
    const bare = String(id)
      .replace(/^cc-/, "")
      .replace(/^CLIMB-/, "");
    return `CLIMB-${bare}`;
  };

  /**
   * Coach created an assignment.
   *
   * whatId is the climb in warehouse form, so it joins to what_dimension AND to
   * climb-completion's what_id. The section goes in metadata and is read
   * through reportbot.events.section_id. It used to go in `result`, but a
   * section id is up to 40 characters and `result` is VARCHAR(20): every one
   * of those rows was rejected by Redshift, and took its Firehose batch down
   * with it.
   */
  const reportAssignmentCreated = async (
    climbId: string,
    sectionId?: string,
  ): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      whatId: toWarehouseClimbId(climbId),
      eventType: EVENT_TYPES.ASSIGNMENT_CREATED,
      metadata: { climbId, sectionId },
    });

  /** Coach edited an existing assignment. Section in metadata, as above. */
  const reportAssignmentEdited = async (
    climbId: string,
    sectionId?: string,
  ): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      whatId: toWarehouseClimbId(climbId),
      eventType: EVENT_TYPES.ASSIGNMENT_EDITED,
      metadata: { climbId, sectionId },
    });

  /**
   * Coach opened a climber's profile.
   *
   * The climber is the whatId. who_id is the coach, which is what attributes
   * the action; the climber is the thing acted on, and the `cli-` prefix keeps
   * it apart from content ids in what_dimension joins — the same shape the
   * server's user-subscription-created already uses. It cannot be `result`:
   * a climber id is 28–40 characters and `result` is VARCHAR(20), so every one
   * of those rows was rejected by Redshift along with its whole batch.
   */
  const reportClimberProfileViewed = async (
    climberId: string,
  ): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      whatId: climberId,
      eventType: EVENT_TYPES.CLIMBER_PROFILE_VIEWED,
      metadata: { climberId },
    });

  /** Coach opened a climber's career plan. The climber is the whatId, as above. */
  const reportCareerPlanViewed = async (
    climberId: string,
    planId?: string,
  ): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      whatId: climberId,
      eventType: EVENT_TYPES.CAREER_PLAN_VIEWED,
      // planId stays in metadata for whoever reads the raw table.
      metadata: { climberId, planId },
    });

  /**
   * Coach gave feedback on a career plan. `status` is the decision —
   * APPROVED or REVISIONS_REQUESTED — which is the one actual result these
   * coach events have.
   */
  const reportCareerPlanFeedback = async (
    climberId: string,
    planId: string | undefined,
    status: "APPROVED" | "REVISIONS_REQUESTED",
  ): Promise<LogEventResponse> =>
    mutation.mutateAsync({
      whatId: climberId,
      eventType: EVENT_TYPES.CAREER_PLAN_FEEDBACK,
      result: status,
      metadata: { climberId, planId },
    });

  const reportClimberReset = async (): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.CLIMBER_RESET,
    });
  };

  /**
   * User logged into the application
   */
  const reportLogin = async (): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.LOGGED_IN,
    });
  };

  /**
   * Completed a career ladder
   * @param ladderId - ID of the completed ladder
   * @param score - Score achieved on the ladder
   * @param maxScore - Maximum possible score for the ladder
   * @param result - Result of the ladder completion
   * @param time - Time taken to complete the ladder
   * @param learnositySessionId - the session id from learnosity
   * @param reportingFields - map of key:value pairs for fields with ReportMe tags on them
   */
  const reportLadderCompletion = async (
    ladderId: string,
    score: number,
    maxScore: number,
    result: string = "success",
    time: number = 0,
    learnositySessionId: string | null = null,
    reportingFields: LogEventMetadata = {},
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "LADDER-" + ladderId,
      eventType: EVENT_TYPES.LADDER_COMPLETION,
      score,
      maxScore,
      result,
      metadata: { time, ladderId, learnositySessionId, ...reportingFields },
    });
  };

  /**
   * Completed a career climb
   * @param climbId - ID of the completed climb
   * @param wasAssigned - Whether the climb was assigned to the user
   */
  const reportClimbCompletion = async (
    climbId: string,
    wasAssigned: boolean,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "CLIMB-" + climbId,
      eventType: EVENT_TYPES.CLIMB_COMPLETION,
      metadata: { wasAssigned, climbId },
    });
  };

  /**
   * User indicated awareness of a career hat
   * @param hatId - ID of the career hat
   * @param heardOf - Whether the user has heard of this hat
   * @param greatFor - RIASEC letters indicating what this hat is great for
   * @param themes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportHeardOfHat = async (
    hatId: string,
    heardOf: boolean,
    greatFor: string | null = null,
    themes: string | null = null,
    clusters: string | null = null,
    workValues: string | null = null,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.HEARD_OF_HAT,
      result: heardOf ? "yes" : "no",
      metadata: { greatFor, themes, hatId, clusters, workValues },
    });
  };

  /**
   * User saved a career hat
   * @param hatId - ID of the career hat
   * @param saved - Whether the hat was saved or unsaved
   * @param greatFor - RIASEC letters indicating what this hat is great for
   * @param themes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportSavedHat = async (
    hatId: string,
    saved: boolean,
    greatFor: string | null = null,
    themes: string | null = null,
    clusters: string | null = null,
    workValues: string | null = null,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.SAVED_HAT,
      result: saved ? "yes" : "no",
      metadata: { greatFor, themes, hatId, clusters, workValues },
    });
  };

  /**
   * Climber earned a hat pack (via onboarding, a badge, or a shout-out).
   * @param hatPackId - The CMS hat-theme (hat pack) id
   * @param source - How the pack was earned: "onboarding" | "badge" | "shoutout"
   */
  const reportEarnedHatPack = async (
    hatPackId: string,
    source: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: hatPackId,
      eventType: EVENT_TYPES.EARNED_HAT_PACK,
      result: source,
      metadata: { hatPackId, source },
    });
  };

  /**
   * Log application errors
   * @param errorDetails - Details about the error that occurred
   */
  const reportError = async (
    errorDetails: LogEventMetadata,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.APPLICATION_ERROR,
      metadata: errorDetails,
    });
  };

  /**
   * User earned experience points
   * @param xp - Amount of XP earned
   * @param totalXp - Total XP the user now has
   * @param reason - Reason for earning the XP
   */
  const reportXPEarned = async (
    xp: number,
    totalXp: number,
    reason: string,
    clusterIds?: string[],
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.EARNED_XP,
      score: xp,
      maxScore: totalXp,
      metadata: {
        reason,
        xp,
        totalXp,
        ...(clusterIds?.length ? { clusterIds } : {}),
      },
    });
  };

  /**
   * Completed an activity
   * @param activityId - ID of the completed activity
   * @param result - Result of the activity completion
   * @param time - Time taken to complete the activity
   */
  const reportActivityCompletion = async (
    activityId: string,
    result: string,
    time: number,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "ACTIVITY-" + activityId,
      eventType: EVENT_TYPES.ACTIVITY_COMPLETION,
      result,
      metadata: { time, activityId },
    });
  };

  /**
   * Explored a career hat by completing all the perks/quirks
   * @param hatId - ID of the explored hat
   * @param greatFor - RIASEC letters indicating what this hat is great for
   * @param themes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportHatExplored = async (
    hatId: string,
    greatFor: string,
    themes: string | null = null,
    clusters: string | null = null,
    firstTime: boolean = false,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.HAT_EXPLORED,
      metadata: {
        greatFor,
        themes,
        hatId,
        clusters,
        firstTime: firstTime ? "true" : "false",
      },
    });
  };

  /**
   * Completed content consumption
   * @param contentId - ID of the content
   * @param blockType - Type of content block
   * @param contentType - Type of content
   * @param contentLevel - Level of the content
   * @param parentId - ID of the parent content
   */
  const recordContentComplete = async (
    contentId: string,
    blockType: string,
    contentType: string,
    contentLevel: string,
    parentId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "CONTENT-" + contentId,
      eventType: EVENT_TYPES.CONTENT_COMPLETION,
      result: "success",
      metadata: {
        blockType,
        contentType,
        contentLevel,
        parentId,
        contentId,
      },
    });
  };

  /**
   * Completed all content within a cluster
   * @param clusterId - ID of the cluster
   */
  const reportClusterContentCompleted = async (
    clusterId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "CLUSTER-" + clusterId,
      eventType: EVENT_TYPES.CLUSTER_CONTENT_COMPLETED,
      result: "success",
      metadata: {
        clusterId,
      },
    });
  };

  /**
   * A recommended cluster has been found for the climber
   * @param clusterId - ID of the recommended cluster
   */
  const reportRecommendedClusterFound = async (
    clusterId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "CLUSTER-" + clusterId,
      eventType: EVENT_TYPES.RECOMMENDED_CLUSTER_FOUND,
      result: "success",
      metadata: { clusterId },
    });
  };

  /**
   * Downloaded a resource
   * @param resourceId - ID of the downloaded resource
   * @param resourceName - Name of the resource
   * @param resourceType - Type of the resource
   * @param category - Category of the resource
   * @param filesize - Size of the file in bytes
   * @param mimeType - MIME type of the file
   */
  const reportResourceDownload = async (
    resourceId: string,
    resourceName: string,
    resourceType: string,
    category: string,
    filesize: number,
    mimeType: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "RESOURCE-" + resourceId,
      eventType: EVENT_TYPES.RESOURCE_DOWNLOAD,
      result: "success",
      metadata: {
        resourceName,
        resourceType,
        category,
        filesize,
        mimeType,
        resourceId,
      },
    });
  };

  /**
   * Occurs when a climber's facet changes for any reason
   * @param facetType - Type of facet that changed
   * @param currentScore - Current score of the facet
   * @param maxScore - Maximum possible score for the facet
   * percentage is auto-calculated from currentScore and maxScore
   */
  const reportFacetChanged = async (
    facetType: string,
    currentScore: number,
    maxScore: number,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.FACET_CHANGED,
      score: currentScore,
      maxScore: maxScore,
      metadata: {
        facetType,
        percentage: Math.round((currentScore / maxScore) * 100),
      },
    });
  };

  /**
   * Watched the given video
   * @param videoId - ID of the video that was watched
   */
  const reportWatchedVideo = async (
    videoId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "VIDEO-" + videoId,
      eventType: EVENT_TYPES.WATCHED_VIDEO,
      result: "success",
      metadata: { videoId },
    });
  };

  const reportListenedToAudio = async (
    audioId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "AUDIO-" + audioId,
      eventType: EVENT_TYPES.LISTENED_TO_AUDIO,
      result: "success",
      metadata: { audioId },
    });
  };

  /**
   * The profile onboarding has been completed
   * @param careerStage - Career stage of the user
   * @param careerConfidence - Career confidence level
   * @param topThemes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportProfileOnboardCompleted = async (
    careerStage: string,
    careerConfidence: string,
    topThemes: string,
    version?: number,
    catchphrase?: string,
    careerTalkFrequency?: string,
    careerNames?: string,
    careerFitKnowledge?: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.PROFILE_ONBOARD_COMPLETED,
      result: "success",
      metadata: {
        careerStage,
        careerConfidence,
        topThemes,
        ...(version !== undefined && { version }),
        ...(catchphrase !== undefined && { catchphrase }),
        ...(careerTalkFrequency !== undefined && { careerTalkFrequency }),
        ...(careerNames !== undefined && { careerNames }),
        ...(careerFitKnowledge !== undefined && { careerFitKnowledge }),
      },
    });
  };

  /**
   * The climber has used the like/dislike/not for me rating widget
   * @param rating - The rating given (like/dislike/not for me)
   * @param hatId - ID of the hat being rated
   * @param greatFor - RIASEC letters, use contains operator (e.g., "RI")
   * @param themes - CSV of theme IDs in format 0001,0002,0003
   * @param firstTime - Whether this is the first time rating this hat
   */
  const reportHatRated = async (
    rating: string,
    hatId: string,
    greatFor: string,
    themes: string,
    firstTime: boolean,
    clusters: string | null = null,
    pathways: string | null = null,
    workValues: string | null = null,
    personalityBadges: string | null = null,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.HAT_RATED,
      result: rating,
      metadata: {
        greatFor,
        themes,
        firstTime,
        hatId,
        clusters,
        pathways,
        workValues,
        personalityBadges,
      },
    });
  };

  /**
   * The climber thumbs-up'd a perk/quirk that has related badges.
   * Fired once per qualifying item at perks/quirks activity submit.
   * `badgeIds` is encoded as a zero-padded comma-joined string (e.g. "0042,0107")
   * so badge criteria can match individual ids via `contains`.
   */
  const reportPerkQuirkBadgeLiked = async (
    perksQuirksId: string,
    badgeIds: string[],
    hatId: string,
    type: "perk" | "quirk",
  ): Promise<LogEventResponse> => {
    const paddedBadgeIds = badgeIds
      .map((id) => String(id).padStart(4, "0"))
      .join(",");
    return mutation.mutateAsync({
      whatId: "PERKQUIRK-" + perksQuirksId,
      eventType: EVENT_TYPES.PERK_QUIRK_BADGE_LIKED,
      result: type,
      metadata: { perksQuirksId, badgeIds: paddedBadgeIds, hatId, type },
    });
  };

  /**
   * Happens when the climber creates a new building block
   * @param blockName - Name of the building block created
   * @param blockCategory - Category of the building block
   */
  const reportAddBuildingBlock = async (
    blockName: string,
    blockCategory: string,
    firstTime: boolean = true,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.ADD_BUILDING_BLOCK,
      result: "success",
      metadata: { blockName, blockCategory, firstTime },
    });
  };

  /**
   * The climber has rated a pathway
   * @param pathwayId - ID of the pathway being rated
   * @param clusterId - ID of the cluster the pathway belongs to
   * @param firstTime - Whether this is the first time rating this pathway
   * @param result - The rating result
   */
  const reportPathwayRated = async (
    pathwayId: string,
    clusterId: string,
    firstTime: boolean,
    result: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "PATHWAY-" + pathwayId,
      eventType: EVENT_TYPES.PATHWAY_RATED,
      result,
      metadata: {
        pathwayId,
        clusterId,
        firstTime: firstTime ? "true" : "false",
      },
    });
  };

  /**
   * Onboard something else has been completed
   * @param careerArea - The career area selected
   */
  const reportOnboardSomethingElse = async (
    careerArea: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.ONBOARD_SOMETHING_ELSE,
      result: "success",
      metadata: { careerArea },
    });
  };

  /**
   * A site visit has been created
   * @param visitId - ID of the created visit
   * @param climberId - ID of the climber the visit is for
   * @param worksite - ID of the worksite
   * @param visitDate - Date of the visit
   * @param visitTime - Time of the visit
   * @param visitorId - ID of the person who created the visit
   */
  const reportSiteVisitCreation = async (
    visitId: string,
    climberId: string,
    worksite: string,
    visitDate: string,
    visitTime: string,
    visitorId?: string | null,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "VISIT-" + visitId,
      eventType: EVENT_TYPES.SITE_VISIT_CREATION,
      result: "success",
      metadata: {
        visitId,
        climberId,
        worksite,
        visitDate,
        visitTime,
        visitorId: visitorId || null,
      },
    });
  };

  /**
   * Career confidence check-in has been completed
   * @param currentStage - Current career stage (0-4)
   * @param currentConfidence - Current confidence level (0-3)
   * @param previousStage - Previous career stage from onboarding/last check-in
   * @param previousConfidence - Previous confidence level from onboarding/last check-in
   * @param changeExplanation - User's explanation for why their answer has/hasn't changed
   */
  const reportCareerConfidenceCheckin = async (
    currentStage: number,
    currentConfidence: number,
    previousStage: number | null,
    previousConfidence: number | null,
    changeExplanation?: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.CAREER_CONFIDENCE_CHECKIN,
      result: "success",
      metadata: {
        currentStage,
        currentConfidence,
        previousStage,
        previousConfidence,
        stageChanged: previousStage !== null && previousStage !== currentStage,
        confidenceChanged:
          previousConfidence !== null &&
          previousConfidence !== currentConfidence,
        changeExplanation: changeExplanation || null,
      },
    });
  };

  return {
    reportLogin,
    reportLadderCompletion,
    reportClimbCompletion,
    reportHatExplored,
    reportHeardOfHat,
    reportListenedToAudio,
    reportSavedHat,
    reportEarnedHatPack,
    reportError,
    reportXPEarned,
    reportActivityCompletion,
    recordContentComplete,
    reportClusterContentCompleted,
    reportRecommendedClusterFound,
    reportResourceDownload,
    reportFacetChanged,
    reportWatchedVideo,
    reportProfileOnboardCompleted,
    reportHatRated,
    reportPerkQuirkBadgeLiked,
    reportAddBuildingBlock,
    reportPathwayRated,
    reportOnboardSomethingElse,
    reportClimberReset,
    reportSiteVisitCreation,
    // Coach actions — see the EVENT_TYPES block for why these exist.
    reportReportOpened,
    reportReportDownloaded,
    reportDashboardViewed,
    reportReportFiltered,
    reportReportDrilldown,
    reportAssignmentCreated,
    reportAssignmentEdited,
    reportClimberProfileViewed,
    reportCareerPlanViewed,
    reportCareerPlanFeedback,
    reportCareerConfidenceCheckin,
    logEvent: mutation,
    recentEvents: eventOutbox.all(),
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
